import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { coursesApi } from '../../api/client'
import { Save, PlusCircle, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'

const LEVELS = ['beginner', 'intermediate', 'advanced']

export default function CourseEditorPage() {
  const { slug } = useParams<{ slug: string }>()
  const isNew = slug === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState({ title: '', subtitle: '', description: '', level: 'intermediate', tags: '', is_free: true, is_enrollment_open: true })
  const [loaded, setLoaded] = useState(false)

  // Load existing course data
  const courseQuery = useQuery({
    queryKey: ['course-edit', slug],
    queryFn: () => coursesApi.get(slug!).then(r => r.data),
    enabled: !isNew && !!slug,
  })

  useEffect(() => {
    if (courseQuery.data && !loaded) {
      const data = courseQuery.data as any
      setForm({ title: data.title, subtitle: data.subtitle || '', description: data.description || '', level: data.level, tags: (data.tags || []).join(', '), is_free: data.is_free, is_enrollment_open: data.is_enrollment_open })
      setLoaded(true)
    }
  }, [courseQuery.data, loaded])

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }
      return isNew ? coursesApi.create(payload) : coursesApi.update(slug!, payload)
    },
    onSuccess: (res) => {
      toast.success(isNew ? 'Course created!' : 'Course saved!')
      qc.invalidateQueries({ queryKey: ['instructor-courses'] })
      if (isNew) navigate(`/instructor/courses/${res.data.slug}/edit`)
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Save failed'),
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }))

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate('/instructor')} className="btn-ghost py-1.5 px-3 text-sm"><ArrowLeft size={15}/> Dashboard</button>
        <h1 className="text-2xl font-extrabold">{isNew ? 'Create New Course' : 'Edit Course'}</h1>
      </div>

      <form className="space-y-5" onSubmit={e => { e.preventDefault(); saveMut.mutate() }}>
        <div>
          <label className="label">Title *</label>
          <input className="input" placeholder="e.g. InfiniBand for HPC Engineers" value={form.title} onChange={set('title')} required />
        </div>
        <div>
          <label className="label">Subtitle</label>
          <input className="input" placeholder="A short description for the catalog card" value={form.subtitle} onChange={set('subtitle')} />
        </div>
        <div>
          <label className="label">Description (supports Markdown)</label>
          <textarea className="input min-h-[150px] resize-y" placeholder="Full course description…" value={form.description} onChange={set('description')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Level</label>
            <select className="input" value={form.level} onChange={set('level')}>
              {LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Tags (comma-separated)</label>
            <input className="input" placeholder="CUDA, GPU, HPC" value={form.tags} onChange={set('tags')} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="is_enrollment_open" checked={form.is_enrollment_open} onChange={set('is_enrollment_open')} className="w-4 h-4 accent-brand-blue" />
          <label htmlFor="is_enrollment_open" className="text-sm text-brand-muted cursor-pointer">Open enrolment (leave off for coming-soon courses)</label>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="is_free" checked={form.is_free} onChange={set('is_free')} className="w-4 h-4 accent-brand-blue" />
          <label htmlFor="is_free" className="text-sm text-brand-muted cursor-pointer">Free course (no paywall)</label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" className="btn-primary" disabled={saveMut.isPending}>
            <Save size={15}/> {saveMut.isPending ? 'Saving…' : 'Save Course'}
          </button>
        </div>
      </form>

      {!isNew && (
        <div className="mt-10 border-t border-brand-border pt-8">
          <h2 className="text-lg font-bold mb-4">Curriculum</h2>
          <p className="text-brand-muted text-sm">Module and lesson management coming in the next release. For now, use the API directly to add modules and lessons.</p>
          <a href="/api/docs" target="_blank" rel="noopener" className="btn-outline mt-3 inline-flex text-sm"><PlusCircle size={14}/> Open API Docs</a>
        </div>
      )}
    </div>
  )
}
